// SchedulesView.jsx — View PURA da /schedules ("Agendas"). Recebe { vm, actions }.
// Contrato: schedules.vm.js (@version 2). Sem fetch, sem formatação, sem regra de negócio:
// veredito, trilha, rótulos e permissões chegam prontos. `useState`/`useRef` só de UI
// (qual "por quê" está aberto, turnos expandidos, realce ao pular para um card).
//
// ═══════════════════════════════════════════════════════════════════════════════
// A PERGUNTA DA TELA: "as pessoas estão conseguindo marcar comigo?"
//
// 1. O VEREDITO DA CONTA vem antes de tudo (VerdictBand). Tem a frase pronta do vm e uma
//    RÉGUA com um traço por agenda (na mesma ordem dos cards): dá para ver "11 de 12" sem ler, e o traço quebrado é
//    mais alto e hachurado (estado nunca só por cor). No caso grave (`callout`) a faixa vira
//    o bloco mais pesado da tela e leva à primeira agenda.
//
// 2. TODO CARD COMEÇA PELO VEREDITO, no mesmo lugar. Três pesos (mesma lógica da PagesView):
//    · recebendo      → CALMO. Uma linha discreta no topo, nada mais colorido que isso.
//    · defeito        → ALTO. Faixa de tint no topo (headline + detail) e a TRILHA de
//                       configuração como corpo do card. Esses cards sobem para o começo do
//                       grupo e ganham largura dupla: com 12 agendas, a quebrada salta.
//    · escolha do dono → SERENO. Ícone de pausa/calendário, texto neutro, sem trilha, sem
//                       vermelho. `isByChoice` nunca vira aviso.
//
// 3. A SEMANA: cada card desenha a fita S-T-Q-Q-S-S-D (shifts[].daysActive). Dia cheio =
//    turno que gera horário; tracejado = há turno, mas o cliente não vê horário (turno
//    desligado, sem serviço, ou a agenda não opera). Mostra saúde pelo que o CLIENTE vê,
//    não por uma bolinha.
//
// 4. A TRILHA é uma linha do tempo: feitos em verde calmo, pendentes com o próximo passo
//    destacado e a ação ali mesmo. O `why` abre sob demanda ("Por que isso importa?").
//    O rótulo de progresso é SEMPRE o `progressLabel` — os segmentos só ilustram.
//
// 5. `lifecycle` é outro eixo: fica como etiqueta neutra no canto ("⏻ Ativa"), nunca
//    fundida ao veredito. O botão de pausar/reativar (`toggleLabel`) fica no rodapé, junto
//    das ações que mudam a agenda — a etiqueta informa, o rodapé age.
//
// 6. @v2 — três linhas de CONTEXTO que a @v1 não tinha, todas com frase pronta do vm:
//    `pagesLabel` (alcance público: no card quebrado é o que dá tamanho ao estrago),
//    `activityLabel` (quem já marcou: separa agenda configurada de agenda viva) e
//    `autoConfirmLabel` (entra na lista de Ajustes como mais um fato).
//
// Somente leitura (`canWrite: false`): cadeado com o `readOnlyReason` e toda escrita
// desabilitada COM tooltip explicando. Agendar e compartilhar continuam (não escrevem na agenda).
//
// Consistência: CARD_SX, TINT, botões, StateBlock e FiltersBar iguais a Serviços/Páginas.
// Neutros de --bg-page/--divider/--text-*; nunca --slate-* (quebra temas escuros).
// Layout interno do card por container query: o mesmo card serve de 390px a 1600px.
import { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Button, ButtonBase, IconButton, Tooltip, TextField, InputAdornment,
  Select, MenuItem, Skeleton, Collapse,
} from '@mui/material';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import FilterAltOffRounded from '@mui/icons-material/FilterAltOffRounded';
import SearchOffRounded from '@mui/icons-material/SearchOffRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import ShareRounded from '@mui/icons-material/ShareRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import EventBusyRounded from '@mui/icons-material/EventBusyRounded';
import UpcomingRounded from '@mui/icons-material/UpcomingRounded';
import PauseCircleRounded from '@mui/icons-material/PauseCircleRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import DateRangeRounded from '@mui/icons-material/DateRangeRounded';
import ViewWeekRounded from '@mui/icons-material/ViewWeekRounded';
import TimerRounded from '@mui/icons-material/TimerRounded';
import PowerSettingsNewRounded from '@mui/icons-material/PowerSettingsNewRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import PlaceRounded from '@mui/icons-material/PlaceRounded';
import DesignServicesRounded from '@mui/icons-material/DesignServicesRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import MoreTimeRounded from '@mui/icons-material/MoreTimeRounded';
import LanguageRounded from '@mui/icons-material/LanguageRounded';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';

/* ═══════════════════════════════════════════════════════════════════════════
   Tokens locais (mesmos papéis das telas irmãs)
   ═══════════════════════════════════════════════════════════════════════════ */

const CARD_SX = {
  bgcolor: 'var(--surface)',
  border: '1px solid',
  borderColor: 'var(--divider)',
  borderRadius: { xs: 'var(--radius-card-mobile)', sm: 'var(--radius-card)' },
  boxShadow: 'var(--shadow-card)',
};

const TINT = {
  success: { bg: 'var(--tint-success-bg)', border: 'var(--tint-success-border)', fg: 'var(--tint-success-fg)', sub: 'var(--tint-success-sub)', main: 'var(--color-success)' },
  warning: { bg: 'var(--tint-warning-bg)', border: 'var(--tint-warning-border)', fg: 'var(--tint-warning-fg)', sub: 'var(--tint-warning-sub)', main: 'var(--color-warning)' },
  error: { bg: 'var(--tint-error-bg)', border: 'var(--tint-error-border)', fg: 'var(--tint-error-fg)', sub: 'var(--tint-error-sub)', main: 'var(--color-error)' },
  info: { bg: 'var(--tint-info-bg)', border: 'var(--tint-info-border)', fg: 'var(--tint-info-fg)', sub: 'var(--tint-info-sub)', main: 'var(--color-info)' },
  neutral: { bg: 'var(--bg-page)', border: 'var(--divider)', fg: 'var(--text-secondary)', sub: 'var(--text-secondary)', main: 'var(--text-secondary)' },
};
const tint = (tone) => TINT[tone] || TINT.neutral;

// CTA comercial (ativação: a primeira agenda). Fallback no verde escuro que já existe.
const CTA_FILL = 'var(--color-cta, var(--tint-success-fg))';
const CTA_FILL_DARK = 'var(--color-cta-dark, color-mix(in srgb, var(--color-cta, var(--tint-success-fg)) 86%, var(--text-primary)))';

const HEAD = { fontWeight: 'var(--fw-bold)' };
const NUM = { fontVariantNumeric: 'tabular-nums' };
const ONE_LINE = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const BREAK = { overflowWrap: 'anywhere' };
const SR_ONLY = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' };
const FOCUS = { '&:focus-visible': { outline: '2px solid var(--color-primary)', outlineOffset: 2 } };
const REDUCED = '@media (prefers-reduced-motion: reduce)';

const PRIMARY_BTN_SX = {
  bgcolor: 'var(--color-primary)', color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.25, minHeight: 42,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-dark)' },
  '&.Mui-disabled': { bgcolor: 'var(--color-primary-light)', color: 'var(--on-primary)', opacity: 0.8 },
  '&:focus-visible': { outline: '2px solid var(--color-primary-dark)', outlineOffset: 2 },
};

const CTA_BTN_SX = {
  bgcolor: CTA_FILL, color: 'var(--on-primary)',
  borderRadius: 'var(--radius-sm)', px: 2.5, minHeight: 46,
  fontWeight: 'var(--fw-bold)', textTransform: 'none', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: CTA_FILL_DARK },
  '&:focus-visible': { outline: `2px solid ${CTA_FILL_DARK}`, outlineOffset: 2 },
};

const OUTLINE_BTN_SX = {
  minHeight: 40, px: 1.75, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  border: '1px solid', borderColor: 'var(--divider)', bgcolor: 'var(--surface)',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
  '&:hover': { borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  ...FOCUS,
};

const QUIET_BTN_SX = {
  minHeight: 40, px: 1.25, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap',
  '&:hover': { color: 'var(--text-primary)', bgcolor: 'var(--bg-page)' },
  ...FOCUS,
};

const LINK_BTN_SX = {
  minHeight: 40, px: 1.25, borderRadius: 'var(--radius-sm)', textTransform: 'none',
  color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)', whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'var(--color-primary-soft)' },
  ...FOCUS,
};

const ICON_BTN_SX = {
  width: 40, height: 40, color: 'var(--text-secondary)',
  '&:hover': { color: 'var(--color-primary-dark)', bgcolor: 'var(--color-primary-soft)' },
  '&.Mui-disabled': { color: 'var(--text-disabled)' },
  ...FOCUS,
};

const DANGER_ICON_BTN_SX = {
  ...ICON_BTN_SX,
  '&:hover': { color: 'var(--color-error)', bgcolor: 'var(--tint-error-bg)' },
};

const rise = (delay = 0) => ({
  '@keyframes schRise': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'none' },
  },
  animation: 'schRise .5s cubic-bezier(.2,.7,.3,1) both',
  animationDelay: `${delay}ms`,
  [REDUCED]: { animation: 'none' },
});

/* O peso visual de uma agenda sai SÓ de `operation` — nunca de `lifecycle`. */
const weightOf = (op) => (op?.isOperating ? 'live' : op?.isByChoice ? 'waiting' : 'alert');
const alertTone = (op) => (op?.status === 'warning' ? 'warning' : 'error');
const waitTone = (op) => (op?.status === 'info' ? 'info' : 'neutral');

const LIFECYCLE_ICON = {
  active: PowerSettingsNewRounded, paused: PauseCircleRounded, scheduled: UpcomingRounded,
  ended: EventBusyRounded, deleted: DeleteOutlineRounded,
};
const FACT_ICON = {
  capacity: GroupsRounded, bookingWindow: DateRangeRounded, daysShown: ViewWeekRounded,
  minimumAdvance: TimerRounded, startDate: EventAvailableRounded, endDate: EventBusyRounded,
  autoConfirm: AutoAwesomeRounded,
};

const DAY_INITIAL = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];
const DAY_NAME = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

// Hachura = "não chega ao cliente". Mesmo desenho na régua, na legenda e na trilha.
const hatch = (c) => `repeating-linear-gradient(135deg, ${c} 0 3px, color-mix(in srgb, ${c} 30%, transparent) 3px 6px)`;

/* ═══════════════════════════════════════════════════════════════════════════
   Peças pequenas
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Linha de contexto do card (@v2): ícone discreto + texto JÁ ESCRITO pelo vm.
 *
 * Serve a dois campos que respondem perguntas diferentes:
 *   · `pagesLabel`    — quantas páginas públicas dependem desta agenda. Num card quebrado é o que
 *                       dá TAMANHO ao problema: "não recebe agendamentos" é abstrato, "e 2 páginas
 *                       suas estão no ar sem conseguir marcar" não é.
 *   · `activityLabel` — quantos agendamentos ela tem pela frente. É a diferença entre uma agenda
 *                       CONFIGURADA e uma agenda VIVA, que o card não sabia mostrar.
 *
 * `herdaCor` deixa a linha assumir a cor do bloco em que está (a faixa do veredito), em vez do
 * cinza secundário — dentro da faixa vermelha, cinza sumiria.
 */
function ContextLine({ icon, children, herdaCor = false }) {
  const Icon = icon;
  const cor = herdaCor ? 'inherit' : 'var(--text-secondary)';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, color: cor }}>
      <Icon sx={{ fontSize: 16, flexShrink: 0 }} aria-hidden="true" />
      <Typography variant="caption" sx={{ color: 'inherit', ...BREAK }}>{children}</Typography>
    </Box>
  );
}

/** Escrita bloqueada: botão desabilitado SEMPRE com o porquê ao alcance. */
function WriteGuard({ canWrite, reason, children }) {
  if (canWrite) return children;
  return (
    <Tooltip title={reason || 'Você não pode alterar esta agenda.'}>
      <Box component="span" sx={{ display: 'inline-flex' }}>{children}</Box>
    </Tooltip>
  );
}

/** Eixo SEPARADO do veredito: o que o dono escolheu. Neutro, pequeno, no canto. */
function LifecycleTag({ lifecycle, label }) {
  const Icon = LIFECYCLE_ICON[lifecycle] || PowerSettingsNewRounded;
  return (
    <Tooltip title="Situação escolhida por você para esta agenda">
      <Box
        tabIndex={0}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
          px: 0.75, py: '2px', borderRadius: 999, color: 'var(--text-secondary)',
          border: '1px solid', borderColor: 'var(--divider)', ...FOCUS,
        }}
      >
        <Icon sx={{ fontSize: 14 }} aria-hidden="true" />
        <Typography variant="caption" sx={{ fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap' }}>{label}</Typography>
      </Box>
    </Tooltip>
  );
}

function ReadOnlyTag({ reason }) {
  return (
    <Tooltip title={reason || ''}>
      <Box
        tabIndex={0}
        aria-label={`Somente leitura. ${reason || ''}`}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
          px: 0.75, py: '2px', borderRadius: 999,
          bgcolor: 'var(--bg-page)', color: 'var(--text-secondary)', ...FOCUS,
        }}
      >
        <LockRounded sx={{ fontSize: 14 }} aria-hidden="true" />
        <Typography variant="caption" sx={{ fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap' }}>Somente leitura</Typography>
      </Box>
    </Tooltip>
  );
}

/** Amostra de estado — o mesmo desenho na régua e na legenda. */
function Swatch({ kind, tone = 'error', height = 10, width = 18 }) {
  const t = tint(tone);
  const base = { width, height, borderRadius: 3, flexShrink: 0, boxSizing: 'border-box' };
  if (kind === 'live') return <Box aria-hidden="true" sx={{ ...base, bgcolor: 'var(--color-success)' }} />;
  if (kind === 'waiting') return <Box aria-hidden="true" sx={{ ...base, border: '1.5px dashed', borderColor: 'var(--text-secondary)' }} />;
  return <Box aria-hidden="true" sx={{ ...base, background: hatch(t.main), boxShadow: `inset 0 0 0 1px ${t.main}` }} />;
}

/* ═══════════════════════════════════════════════════════════════════════════
   A SEMANA — o que o cliente encontra, dia a dia
   ═══════════════════════════════════════════════════════════════════════════ */

function WeekRibbon({ shifts, mode, tone = 'error' }) {
  const planned = Array(7).fill(false);
  const offered = Array(7).fill(false);
  (shifts || []).forEach((s) => (s.daysActive || []).forEach((on, i) => {
    if (!on) return;
    planned[i] = true;
    if (mode === 'live' && s.isActive && !s.warning) offered[i] = true;
  }));
  const t = tint(mode === 'alert' ? tone : 'neutral');
  const hasPlannedOnly = planned.some((p, i) => p && !offered[i]);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box component="ul" aria-label="Dias da semana" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', gap: 0.5, maxWidth: 280 }}>
        {DAY_INITIAL.map((d, i) => {
          const state = offered[i] ? 'on' : planned[i] ? 'planned' : 'off';
          const srText = state === 'on' ? 'tem horário para o cliente' : state === 'planned' ? 'tem turno, mas não gera horário' : 'sem atendimento';
          return (
            <Box
              component="li"
              key={i}
              aria-label={`${DAY_NAME[i]}: ${srText}`}
              sx={{
                flex: '1 1 0', minWidth: 0, height: 28, ml: i === 5 ? 0.5 : 0,
                borderRadius: '6px', boxSizing: 'border-box',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background-color .3s, border-color .3s',
                ...(state === 'on' && { bgcolor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)' }),
                ...(state === 'planned' && { border: '1.5px dashed', borderColor: t.main, color: t.fg }),
                ...(state === 'off' && { bgcolor: 'var(--bg-page)', color: 'var(--text-disabled)' }),
              }}
            >
              <Typography variant="caption" aria-hidden="true" sx={{ fontWeight: state === 'off' ? 'var(--fw-regular)' : 'var(--fw-bold)' }}>
                {d}
              </Typography>
            </Box>
          );
        })}
      </Box>
      {mode === 'alert' && (
        <Typography variant="caption" sx={{ display: 'block', color: t.sub, mt: 0.75 }}>
          {hasPlannedOnly
            ? 'O cliente não vê horário nenhum desta agenda. Dias tracejados têm turno, mas não geram horário.'
            : 'O cliente não vê horário nenhum desta agenda.'}
        </Typography>
      )}
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Turnos e ajustes (contexto — recuam em tela estreita)
   ═══════════════════════════════════════════════════════════════════════════ */

function ShiftRow({ shift, canWrite, onEdit }) {
  const inner = (
    <>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1 }}>
          <Typography
            variant="subtitle2"
            component="span"
            sx={{ ...HEAD, ...NUM, color: shift.isActive ? 'var(--text-primary)' : 'var(--text-secondary)', textDecoration: shift.isActive ? 'none' : 'line-through' }}
          >
            {shift.timeLabel}
          </Typography>
          <Typography variant="caption" component="span" sx={{ color: 'var(--text-secondary)' }}>{shift.daysLabel}</Typography>
        </Box>
        {(shift.serviceNames?.length > 0 || shift.breakLabel) && (
          <Typography variant="caption" component="p" sx={{ color: 'var(--text-secondary)', ...ONE_LINE }}>
            {[...(shift.serviceNames || []), shift.breakLabel].filter(Boolean).join(' · ')}
          </Typography>
        )}
        {shift.warning && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.25, color: 'var(--tint-warning-fg)' }}>
            <WarningAmberRounded sx={{ fontSize: 14, mt: '2px', flexShrink: 0 }} aria-hidden="true" />
            <Typography variant="caption" component="p" sx={{ textWrap: 'pretty' }}>{shift.warning}</Typography>
          </Box>
        )}
      </Box>
      {canWrite && (
        <EditRounded
          className="shiftEdit"
          aria-hidden="true"
          sx={{ fontSize: 18, color: 'var(--color-primary-dark)', opacity: 0, transform: 'translateX(-4px)', transition: 'opacity .15s, transform .15s', flexShrink: 0 }}
        />
      )}
    </>
  );
  const common = {
    display: 'flex', alignItems: 'center', gap: 1, width: '100%', minHeight: 44,
    px: 1, py: 0.75, mx: -1, borderRadius: 'var(--radius-sm)', textAlign: 'left', boxSizing: 'content-box',
  };
  if (!canWrite) return <Box sx={common}>{inner}</Box>;
  return (
    <ButtonBase
      onClick={onEdit}
      aria-label={`Editar turno ${shift.daysLabel}, ${shift.timeLabel}`}
      sx={{
        ...common, justifyContent: 'flex-start',
        transition: 'background-color .15s',
        '&:hover, &:focus-visible': { bgcolor: 'var(--bg-page)' },
        '&:hover .shiftEdit, &:focus-visible .shiftEdit': { opacity: 1, transform: 'none' },
        ...FOCUS,
      }}
    >
      {inner}
    </ButtonBase>
  );
}

function ShiftsBlock({ item, a, open, onToggle, max = 2 }) {
  const shifts = item.shifts || [];
  if (shifts.length === 0) return null;
  const shown = open ? shifts : shifts.slice(0, max);
  const hidden = shifts.length - shown.length;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {shown.map((s) => (
          <ShiftRow key={s.id} shift={s} canWrite={item.canWrite} onEdit={() => a.onEditShift?.(item.id, s.id)} />
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25, mx: -1.25 }}>
        {(hidden > 0 || open) && shifts.length > max && (
          <Button
            onClick={onToggle}
            aria-expanded={open}
            endIcon={<ExpandMoreRounded sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />}
            sx={QUIET_BTN_SX}
          >
            {open ? 'Mostrar menos' : `Ver todos · ${item.shiftsLabel}`}
          </Button>
        )}
        {item.canWrite && (
          <Button onClick={() => a.onAddShift?.(item.id)} startIcon={<MoreTimeRounded sx={{ fontSize: 18 }} />} sx={QUIET_BTN_SX}>
            Novo turno
          </Button>
        )}
      </Box>
    </Box>
  );
}

/** Ajustes: o que o dono configurou aparece; o padrão recua para dentro do "Ajustes". */
function FactsBlock({ facts, autoConfirmLabel, open, onToggle, id }) {
  /* @v2 — `autoConfirmLabel` entra como um FATO como os outros, não como linha avulsa: é
     exatamente da mesma natureza (uma configuração da agenda que muda o que o cliente vive) e
     assim herda de graça o comportamento já pensado — visível no desktop, recolhido dentro de
     "Ajustes da agenda" no mobile. Fica sempre como `custom` porque, padrão ou não, confirmar
     à mão vs. automático é a resposta que o dono mais procura aqui. */
  const list = autoConfirmLabel
    ? [{ id: 'autoConfirm', label: autoConfirmLabel, isDefault: false, help: 'Define se o horário já nasce confirmado ou fica esperando você aprovar.' }, ...(facts || [])]
    : (facts || []);
  if (list.length === 0) return null;
  const custom = list.filter((f) => !f.isDefault);
  const defaults = list.filter((f) => f.isDefault);
  const panelId = `facts-${id}`;
  return (
    <Box sx={{ minWidth: 0 }}>
      {custom.length > 0 && (
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 0.5, mb: 0.5 }}>
          {custom.map((f) => {
            const Icon = FACT_ICON[f.id] || TuneRounded;
            return (
              <Tooltip key={f.id} title={f.help} placement="top-start">
                <Box tabIndex={0} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, width: 'fit-content', maxWidth: '100%', borderRadius: '6px', ...FOCUS }}>
                  <Icon sx={{ fontSize: 16, color: 'var(--text-secondary)', flexShrink: 0 }} aria-hidden="true" />
                  <Typography variant="caption" sx={{ color: 'var(--text-primary)', ...ONE_LINE }}>{f.label}</Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      )}
      <Box sx={{ mx: -1.25, display: { xs: 'block', sm: defaults.length ? 'block' : 'none' } }}>
        <Button
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={panelId}
          startIcon={<TuneRounded sx={{ fontSize: 18 }} />}
          endIcon={<ExpandMoreRounded sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />}
          sx={QUIET_BTN_SX}
        >
          Ajustes da agenda
        </Button>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box id={panelId} component="ul" sx={{ listStyle: 'none', m: 0, mt: 0.5, p: 1.25, bgcolor: 'var(--bg-page)', borderRadius: 'var(--radius-sm)', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {list.map((f) => {
            const Icon = FACT_ICON[f.id] || TuneRounded;
            return (
              <Box
                component="li"
                key={f.id}
                sx={{ display: { xs: 'flex', sm: f.isDefault ? 'flex' : 'none' }, alignItems: 'flex-start', gap: 1 }}
              >
                <Icon sx={{ fontSize: 16, color: 'var(--text-secondary)', mt: '2px', flexShrink: 0 }} aria-hidden="true" />
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.75 }}>
                    <Typography variant="caption" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>{f.label}</Typography>
                    {f.isDefault && <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>· padrão</Typography>}
                  </Box>
                  <Typography variant="caption" component="p" sx={{ color: 'var(--text-secondary)' }}>{f.help}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   A TRILHA — o que falta para o cliente conseguir marcar
   ═══════════════════════════════════════════════════════════════════════════ */

function ProgressSegments({ done, total, tone, label }) {
  const t = tint(tone);
  return (
    <Box role="img" aria-label={label} sx={{ display: 'flex', gap: 0.5, width: '100%', maxWidth: 260 }}>
      {Array.from({ length: Math.max(total, 0) }).map((_, i) => (
        <Box
          key={i}
          aria-hidden="true"
          sx={{
            flex: '1 1 0', height: 6, borderRadius: 3,
            transition: 'background .4s',
            ...(i < done ? { bgcolor: 'var(--color-success)' } : { background: hatch(t.main), opacity: 0.85 }),
          }}
        />
      ))}
    </Box>
  );
}

function TrailItem({ c, isNext, last, nextDone, tone, open, onToggle, onAction, canWrite, reason, whyId }) {
  const t = tint(tone);
  // Micro-interação: o item que acabou de ficar pronto "assenta" com um pulso curto.
  const prevDone = useRef(c.done);
  const [justDone, setJustDone] = useState(false);
  useEffect(() => {
    let tm;
    if (!prevDone.current && c.done) {
      setJustDone(true);
      tm = setTimeout(() => setJustDone(false), 900);
    }
    prevDone.current = c.done;
    return () => clearTimeout(tm);
  }, [c.done]);

  const hasAction = !c.done && !!c.actionLabel && !!c.actionId;

  return (
    <Box
      component="li"
      sx={{
        position: 'relative', pl: 5, pb: last ? 0 : 1.5,
        '&::before': last ? undefined : {
          content: '""', position: 'absolute', left: 12, top: 30, bottom: 2, width: 2, borderRadius: 1,
          bgcolor: c.done && nextDone ? 'var(--tint-success-border)' : 'var(--divider)',
        },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute', left: 0, top: 2, width: 26, height: 26, borderRadius: '50%', boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background-color .4s, border-color .4s',
          ...(c.done
            ? { bgcolor: 'var(--tint-success-bg)', border: '1.5px solid', borderColor: 'var(--tint-success-border)', color: 'var(--tint-success-fg)' }
            : { bgcolor: isNext ? t.bg : 'var(--surface)', border: '2px solid', borderColor: t.main, color: t.fg }),
          ...(justDone && {
            '@keyframes schSettle': { '0%': { transform: 'scale(.6)' }, '60%': { transform: 'scale(1.18)' }, '100%': { transform: 'scale(1)' } },
            animation: 'schSettle .6s cubic-bezier(.2,.7,.3,1)',
            [REDUCED]: { animation: 'none' },
          }),
          ...(isNext && {
            '@keyframes schNext': { '0%': { boxShadow: `0 0 0 0 ${t.border}` }, '70%, 100%': { boxShadow: '0 0 0 7px transparent' } },
            animation: 'schNext 2.6s ease-out 3',
            [REDUCED]: { animation: 'none' },
          }),
        }}
      >
        {c.done
          ? <CheckRounded sx={{ fontSize: 16 }} />
          : <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isNext ? t.main : 'transparent' }} />}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 1.5, rowGap: 0.75 }}>
        <Box sx={{ minWidth: 0, flex: '1 1 180px', pt: '3px' }}>
          {isNext && (
            <Typography variant="overline" component="p" sx={{ color: t.fg, fontWeight: 'var(--fw-bold)', letterSpacing: '.08em' }}>
              Próximo passo
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Typography
              variant={c.done ? 'body2' : 'subtitle2'}
              component="p"
              sx={{ color: c.done ? 'var(--text-secondary)' : 'var(--text-primary)', fontWeight: c.done ? 'var(--fw-regular)' : 'var(--fw-bold)', textWrap: 'pretty' }}
            >
              <Box component="span" sx={SR_ONLY}>{c.done ? 'Feito: ' : 'Pendente: '}</Box>
              {c.label}
            </Typography>
            {c.done && (
              <Tooltip title="Por que isso importa?">
                <IconButton onClick={onToggle} aria-expanded={open} aria-controls={whyId} aria-label={`Por que "${c.label}" importa`} sx={{ ...ICON_BTN_SX, my: -1 }}>
                  <HelpOutlineRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {!c.done && (
            <Button
              onClick={onToggle}
              aria-expanded={open}
              aria-controls={whyId}
              endIcon={<ExpandMoreRounded sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />}
              sx={{ ...QUIET_BTN_SX, minHeight: 32, px: 0.75, ml: -0.75, mt: 0.25, color: t.sub }}
            >
              Por que isso importa?
            </Button>
          )}
        </Box>
        {hasAction && (
          <WriteGuard canWrite={canWrite} reason={reason}>
            <Button
              onClick={onAction}
              disabled={!canWrite}
              disableElevation
              endIcon={<ArrowForwardRounded sx={{ fontSize: 18, transition: 'transform .2s' }} />}
              sx={{ ...(isNext ? PRIMARY_BTN_SX : OUTLINE_BTN_SX), minHeight: 40, '&:hover .MuiButton-endIcon svg': { transform: 'translateX(3px)' } }}
            >
              {c.actionLabel}
            </Button>
          </WriteGuard>
        )}
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box
          id={whyId}
          sx={{ mt: 1, p: 1.5, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--bg-page)', border: '1px solid', borderColor: 'var(--divider)' }}
        >
          <Typography variant="body2" sx={{ color: 'var(--text-primary)', whiteSpace: 'pre-line', textWrap: 'pretty' }}>
            {c.why}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}

function Trail({ item, tone, a, openWhy, onToggleWhy }) {
  const op = item.operation;
  // Feitos primeiro, pendentes no fim: a trilha lê como progresso (o que já está pronto → o que falta)
  // e casa com os segmentos. Partição ESTÁVEL: os pendentes mantêm a ordem de resolução do vm.
  const src = op.checklist || [];
  const list = [...src.filter((c) => c.done), ...src.filter((c) => !c.done)];
  const nextId = (list.find((c) => !c.done && c.actionId) || list.find((c) => !c.done) || {}).id;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ mb: 1.75, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography variant="subtitle1" component="p" sx={{ ...HEAD, color: 'var(--text-primary)', textWrap: 'pretty' }}>
          {op.progressLabel}
        </Typography>
        <ProgressSegments done={op.doneCount} total={op.totalCount} tone={tone} label={op.progressLabel} />
      </Box>
      <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {list.map((c, i) => (
          <TrailItem
            key={c.id}
            c={c}
            isNext={c.id === nextId}
            last={i === list.length - 1}
            nextDone={!!list[i + 1]?.done}
            tone={tone}
            open={openWhy === c.id}
            onToggle={() => onToggleWhy(c.id)}
            onAction={() => a.onChecklistAction?.(item.id, c.actionId)}
            canWrite={item.canWrite}
            reason={item.readOnlyReason}
            whyId={`why-${item.id}-${c.id}`}
          />
        ))}
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Cards
   ═══════════════════════════════════════════════════════════════════════════ */

function CardHead({ item, headingId }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Typography id={headingId} variant="subtitle1" component="h3" sx={{ ...HEAD, ...BREAK, color: 'var(--text-primary)', minWidth: 0 }}>
          {item.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end', mt: '2px' }}>
          {!item.canWrite && <ReadOnlyTag reason={item.readOnlyReason} />}
          <LifecycleTag lifecycle={item.lifecycle} label={item.lifecycleLabel} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25, mt: 0.5, color: 'var(--text-secondary)' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <PersonRounded sx={{ fontSize: 15 }} aria-hidden="true" />
          <Typography variant="caption" sx={ONE_LINE}>{item.professionalName}</Typography>
        </Box>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%' }}>
          <PlaceRounded sx={{ fontSize: 15 }} aria-hidden="true" />
          <Typography variant="caption" sx={ONE_LINE}>{item.placeName}</Typography>
        </Box>
      </Box>
      {item.description && (
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.75, ...ONE_LINE }}>
          {item.description}
        </Typography>
      )}
    </Box>
  );
}

function CardFooter({ item, a }) {
  const w = item.canWrite;
  const r = item.readOnlyReason;
  /* @v2 — pausar/reativar. Sem isto a agenda pausada não tinha volta POR ESTA TELA: o dono via
     "Pausada" no canto e precisava abrir a edição para desfazer. Fica ao lado de "Agendar", do
     lado das ações que mudam o estado da agenda, e não entre os ícones de editar/excluir.
     O rótulo vem pronto do vm (é ele que sabe o sentido); aqui só escolhemos a seta. O vm já
     manda `null` quando a ação não cabe — o `w` abaixo é a mesma trava dos outros botões. */
  const ToggleIcon = item.lifecycle === 'active' ? PauseCircleRounded : PlayArrowRounded;
  return (
    <Box sx={{ mt: 'auto', px: { xs: 1.25, sm: 1.5 }, py: 0.75, borderTop: '1px solid', borderColor: 'var(--divider)', display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      {item.canBook && (
        <Button onClick={() => a.onBook?.(item.id)} startIcon={<EventAvailableRounded sx={{ fontSize: 18 }} />} sx={LINK_BTN_SX}>
          Agendar
        </Button>
      )}
      {item.toggleLabel && (w ? (
        <Button onClick={() => a.onToggleLifecycle?.(item.id)} startIcon={<ToggleIcon sx={{ fontSize: 18 }} />} sx={LINK_BTN_SX}>
          {item.toggleLabel}
        </Button>
      ) : (
        <WriteGuard canWrite={false} reason={r}>
          <Button disabled startIcon={<ToggleIcon sx={{ fontSize: 18 }} />} sx={LINK_BTN_SX}>
            {item.toggleLabel}
          </Button>
        </WriteGuard>
      ))}
      <Box sx={{ flex: 1 }} />
      <Tooltip title="Link público da agenda">
        <IconButton aria-label={`Link público de ${item.name}`} onClick={() => a.onShare?.(item.id)} sx={ICON_BTN_SX}>
          <ShareRounded sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
      {w ? (
        <Tooltip title="Editar agenda">
          <IconButton aria-label={`Editar ${item.name}`} onClick={() => a.onEdit?.(item.id)} sx={ICON_BTN_SX}>
            <EditRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <WriteGuard canWrite={false} reason={r}>
          <IconButton aria-label={`Editar ${item.name} (indisponível: somente leitura)`} disabled sx={ICON_BTN_SX}>
            <EditRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </WriteGuard>
      )}
      {w ? (
        <Tooltip title="Excluir agenda">
          <IconButton aria-label={`Excluir ${item.name}`} onClick={() => a.onDelete?.(item.id)} sx={DANGER_ICON_BTN_SX}>
            <DeleteOutlineRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <WriteGuard canWrite={false} reason={r}>
          <IconButton aria-label={`Excluir ${item.name} (indisponível: somente leitura)`} disabled sx={DANGER_ICON_BTN_SX}>
            <DeleteOutlineRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </WriteGuard>
      )}
    </Box>
  );
}

const cardShell = (flash, celebrate, borderColor, hoverBorder) => ({
  ...CARD_SX, borderColor, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0,
  containerType: 'inline-size', outline: 'none', scrollMarginTop: 96,
  transition: 'box-shadow .2s, border-color .2s',
  '&:hover': { boxShadow: 'var(--shadow-hover)', borderColor: hoverBorder },
  ...(flash && { boxShadow: '0 0 0 3px var(--color-primary-light), var(--shadow-hover)' }),
  ...(celebrate && {
    '@keyframes schWin': { '0%': { boxShadow: '0 0 0 0 var(--tint-success-border)' }, '40%': { boxShadow: '0 0 0 8px var(--tint-success-border)' }, '100%': { boxShadow: 'var(--shadow-card)' } },
    animation: 'schWin 1.6s ease-out',
    [REDUCED]: { animation: 'none' },
  }),
});

/** Card de peso ALTO: não opera e NÃO foi escolha. A trilha é o corpo. */
function AlertCard({ item, a, ui, cardRef, flash, delay }) {
  const op = item.operation;
  const tone = alertTone(op);
  const t = tint(tone);
  const headingId = `sch-${item.id}`;
  return (
    <Box
      component="article"
      ref={cardRef}
      tabIndex={-1}
      aria-labelledby={headingId}
      sx={{ ...cardShell(flash, false, t.border, t.main), ...rise(delay) }}
    >
      <Box
        sx={{
          bgcolor: t.bg, borderBottom: '1px solid', borderColor: t.border,
          px: { xs: 2, sm: 2.25 }, py: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5,
        }}
      >
        <Box sx={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, bgcolor: 'var(--surface)', border: '1px solid', borderColor: t.border, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EventBusyRounded sx={{ fontSize: 22, color: t.fg }} aria-hidden="true" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" component="p" sx={{ ...HEAD, color: t.fg, textWrap: 'pretty' }}>{op.headline}</Typography>
          {op.detail && <Typography variant="body2" sx={{ color: t.fg, textWrap: 'pretty' }}>{op.detail}</Typography>}
          {/* @v2 — dentro da faixa do defeito porque é aqui que ele ganha TAMANHO: "não recebe
              agendamentos" é abstrato; "e 2 páginas suas estão no ar assim" é o custo real. */}
          {item.pagesLabel && (
            <Box sx={{ mt: 0.5 }}>
              <ContextLine icon={LanguageRounded} herdaCor>{item.pagesLabel}</ContextLine>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ p: { xs: 2, sm: 2.25 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <CardHead item={item} headingId={headingId} />
        <Box
          sx={{
            display: 'grid', gap: 2.5, gridTemplateColumns: 'minmax(0, 1fr)',
            '@container (min-width: 760px)': { gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)', gap: 3.5 },
          }}
        >
          <Trail item={item} tone={tone} a={a} openWhy={ui.openWhy} onToggleWhy={ui.toggleWhy} />
          <Box
            sx={{
              minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5,
              pt: 2, borderTop: '1px solid', borderColor: 'var(--divider)',
              '@container (min-width: 760px)': { pt: 0, borderTop: 'none', pl: 3.5, borderLeft: '1px solid', borderLeftColor: 'var(--divider)' },
            }}
          >
            <Typography variant="overline" component="p" sx={{ color: 'var(--text-secondary)', fontWeight: 'var(--fw-bold)', letterSpacing: '.08em' }}>
              A semana desta agenda
            </Typography>
            <WeekRibbon shifts={item.shifts} mode="alert" tone={tone} />
            {/* @v2 — numa agenda quebrada, quem JÁ marcou é a parte urgente: essas pessoas têm
                horário e a agenda parou. Fica colado na semana, que é onde se olha por isso. */}
            {item.activityLabel && <ContextLine icon={EventAvailableRounded}>{item.activityLabel}</ContextLine>}
            <ShiftsBlock item={item} a={a} open={ui.shiftsOpen} onToggle={ui.toggleShifts} />
            <FactsBlock facts={item.facts} autoConfirmLabel={item.autoConfirmLabel} open={ui.factsOpen} onToggle={ui.toggleFacts} id={item.id} />
          </Box>
        </Box>
      </Box>
      <CardFooter item={item} a={a} />
    </Box>
  );
}

/** Card calmo (recebendo) ou sereno (em espera por escolha). */
function CalmCard({ item, a, ui, cardRef, flash, celebrate, delay }) {
  const op = item.operation;
  const live = op.isOperating;
  const tone = live ? 'success' : waitTone(op);
  const t = tint(tone);
  const VerdictIcon = live ? CheckCircleRounded : op.status === 'info' ? UpcomingRounded : PauseCircleRounded;
  const headingId = `sch-${item.id}`;
  return (
    <Box
      component="article"
      ref={cardRef}
      tabIndex={-1}
      aria-labelledby={headingId}
      sx={{ ...cardShell(flash, celebrate, 'var(--divider)', 'var(--color-primary-light)'), ...rise(delay) }}
    >
      <Box sx={{ p: { xs: 2, sm: 2.25 }, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, color: t.fg }}>
          <VerdictIcon sx={{ fontSize: 18, mt: '1px', flexShrink: 0 }} aria-hidden="true" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" component="p" sx={{ ...HEAD, color: t.fg }}>{op.headline}</Typography>
            {!live && op.detail && (
              <Typography variant="caption" component="p" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty' }}>{op.detail}</Typography>
            )}
          </Box>
        </Box>

        <CardHead item={item} headingId={headingId} />
        <WeekRibbon shifts={item.shifts} mode={live ? 'live' : 'waiting'} />

        {!live && op.progressLabel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'var(--text-secondary)' }}>
            <CheckRounded sx={{ fontSize: 16, color: 'var(--tint-success-fg)' }} aria-hidden="true" />
            <Typography variant="caption">{op.progressLabel}</Typography>
          </Box>
        )}

        {/* @v2 — a diferença entre uma agenda CONFIGURADA e uma agenda VIVA. A @v1 sabia dizer
            que ela "está recebendo", mas não se alguém de fato marcou, nem quanto do que está
            no ar depende dela. Duas linhas quietas, na ordem em que a pergunta aparece:
            primeiro o que já foi marcado, depois o alcance público. */}
        {(item.activityLabel || item.pagesLabel) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {item.activityLabel && <ContextLine icon={EventAvailableRounded}>{item.activityLabel}</ContextLine>}
            {item.pagesLabel && <ContextLine icon={LanguageRounded}>{item.pagesLabel}</ContextLine>}
          </Box>
        )}

        <ShiftsBlock item={item} a={a} open={ui.shiftsOpen} onToggle={ui.toggleShifts} />
        <FactsBlock facts={item.facts} autoConfirmLabel={item.autoConfirmLabel} open={ui.factsOpen} onToggle={ui.toggleFacts} id={item.id} />
      </Box>
      <CardFooter item={item} a={a} />
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   O VEREDITO DA CONTA
   ═══════════════════════════════════════════════════════════════════════════ */

function Roster({ roster, onJump }) {
  if (!roster || roster.length < 2) return null;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="h2" sx={SR_ONLY}>Suas agendas, uma a uma</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', height: 28 }}>
        {roster.map((r) => {
          const t = tint(r.tone);
          const bar = r.weight === 'live'
            ? { height: 8, bgcolor: 'var(--color-success)' }
            : r.weight === 'waiting'
              ? { height: 8, border: '1.5px dashed', borderColor: 'var(--text-secondary)' }
              : { height: 16, background: hatch(t.main), boxShadow: `inset 0 0 0 1px ${t.main}` };
          return (
            <Tooltip key={r.id} title={`${r.name} — ${r.headline}`}>
              <ButtonBase
                tabIndex={-1}
                aria-hidden="true"
                onClick={() => onJump(r.id)}
                sx={{
                  flex: '1 1 0', minWidth: 6, maxWidth: 72, height: 28, display: 'flex', alignItems: 'center',
                  '&:hover > span': { transform: 'scaleY(1.35)' },
                }}
              >
                <Box component="span" sx={{ display: 'block', width: '100%', borderRadius: 3, boxSizing: 'border-box', transition: 'transform .15s', ...bar }} />
              </ButtonBase>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

function StatTile({ kind, tone, value, label, onClick }) {
  const t = tint(kind === 'alert' ? tone : kind === 'live' ? 'success' : 'neutral');
  const content = (
    <>
      <Swatch kind={kind} tone={tone} width={14} height={kind === 'alert' ? 14 : 8} />
      <Typography variant="subtitle1" component="span" sx={{ ...HEAD, ...NUM, color: kind === 'waiting' ? 'var(--text-primary)' : t.fg }}>
        {value}
      </Typography>
      <Typography variant="body2" component="span" sx={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{label}</Typography>
    </>
  );
  const sx = {
    display: 'inline-flex', alignItems: 'center', gap: 1, minHeight: 44, px: 1.5, py: 0.75,
    borderRadius: 'var(--radius-sm)', bgcolor: 'var(--surface)', border: '1px solid',
    borderColor: kind === 'alert' ? t.border : 'var(--divider)',
  };
  if (!onClick) return <Box sx={sx}>{content}</Box>;
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`${value} ${label}. Ir para a primeira.`}
      sx={{ ...sx, transition: 'border-color .15s, transform .15s', '&:hover': { borderColor: t.main, transform: 'translateY(-1px)' }, ...FOCUS, [REDUCED]: { '&:hover': { transform: 'none' } } }}
    >
      {content}
    </ButtonBase>
  );
}

function VerdictBand({ summary, roster, onJump, firstAlertId, firstAlertTone }) {
  const s = summary || {};
  if (!s.headline) return null;
  const grave = !!s.callout;
  const t = tint(s.status);
  const calm = s.status === 'success' || s.status === 'neutral';
  const HeadIcon = { success: EventAvailableRounded, warning: WarningAmberRounded, error: EventBusyRounded }[s.status] || CalendarMonthRounded;

  const stats = [
    { key: 'op', kind: 'live', value: s.operating, label: 'recebendo agendamento', show: true },
    { key: 'is', kind: 'alert', value: s.withIssues, label: 'com pendência', show: s.withIssues > 0, jump: !!firstAlertId },
    { key: 'bc', kind: 'waiting', value: s.byChoice, label: 'em espera por escolha sua', show: s.byChoice > 0 },
  ].filter((x) => x.show);

  return (
    <Box
      component="section"
      aria-label="Situação das suas agendas"
      sx={{
        ...CARD_SX, ...rise(0),
        bgcolor: calm ? 'var(--surface)' : t.bg,
        borderColor: calm ? 'var(--divider)' : t.border,
        p: { xs: 2, sm: grave ? 3 : 2.25 }, mb: 'var(--space-3)',
        display: 'flex', flexDirection: 'column', gap: grave ? 2.5 : 1.75,
      }}
    >
      {grave ? (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.75, sm: 2.5 }, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 56, height: 56, borderRadius: '50%', flexShrink: 0, bgcolor: t.main,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              '@keyframes schHalo': {
                '0%': { boxShadow: `0 0 0 0 ${t.border}` },
                '30%': { boxShadow: '0 0 0 12px transparent' },
                '31%': { boxShadow: `0 0 0 0 ${t.border}` },
                '65%, 100%': { boxShadow: '0 0 0 12px transparent' },
              },
              animation: 'schHalo 2.4s ease-out .6s 1 both',
              [REDUCED]: { animation: 'none' },
            }}
          >
            <EventBusyRounded sx={{ fontSize: 30, color: 'var(--on-primary)' }} />
          </Box>
          <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: 760 }}>
            <Typography variant="h5" component="p" role="alert" sx={{ ...HEAD, color: t.fg, textWrap: 'pretty' }}>
              {s.callout}
            </Typography>
            <Typography variant="body1" sx={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)', textWrap: 'pretty' }}>
              {s.headline}
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)', textWrap: 'pretty' }}>
              Cada agenda abaixo mostra exatamente o que falta — e o conserto está no próprio card.
            </Typography>
            {firstAlertId && (
              <Box sx={{ mt: 1 }}>
                <Button onClick={() => onJump(firstAlertId, true)} disableElevation endIcon={<ArrowForwardRounded />} sx={PRIMARY_BTN_SX}>
                  Começar pela primeira agenda
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <HeadIcon sx={{ fontSize: 30, color: calm ? 'var(--tint-success-fg)' : t.fg, flexShrink: 0 }} aria-hidden="true" />
          <Typography variant="h6" component="p" sx={{ ...HEAD, color: calm ? 'var(--text-primary)' : t.fg, textWrap: 'pretty' }}>
            {s.headline}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: { xs: 1.25, md: 2.5 } }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {stats.map((x) => (
            <StatTile
              key={x.key}
              kind={x.kind}
              tone={firstAlertTone}
              value={x.value}
              label={x.label}
              onClick={x.jump ? () => onJump(firstAlertId) : undefined}
            />
          ))}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Roster roster={roster} onJump={onJump} />
        </Box>
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Filtros, grupos, estados
   ═══════════════════════════════════════════════════════════════════════════ */

function FiltersBar({ filters, a }) {
  const f = filters || {};
  const sel = { flex: '1 1 190px', minWidth: 170, borderRadius: 'var(--radius-sm)', bgcolor: 'var(--surface)' };
  return (
    <Box sx={{ ...CARD_SX, p: { xs: 1.5, sm: 1.75 }, mb: 'var(--space-3)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>
      <TextField
        value={f.searchText || ''}
        onChange={(e) => a.onSearchChange?.(e.target.value)}
        placeholder="Buscar agenda por nome…"
        size="small"
        inputProps={{ 'aria-label': 'Buscar agenda' }}
        InputProps={{ startAdornment: (<InputAdornment position="start"><SearchRounded sx={{ fontSize: 20, color: 'var(--text-secondary)' }} /></InputAdornment>) }}
        sx={{ flex: '2 1 240px', minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 'var(--radius-sm)' } }}
      />
      {(f.professionalOptions || []).length > 0 && (
        <Select value={f.professional || ''} displayEmpty onChange={(e) => a.onProfessionalChange?.(e.target.value)} size="small" inputProps={{ 'aria-label': 'Filtrar por profissional' }} sx={sel}>
          {f.professionalOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      )}
      {(f.placeOptions || []).length > 0 && (
        <Select value={f.place || ''} displayEmpty onChange={(e) => a.onPlaceChange?.(e.target.value)} size="small" inputProps={{ 'aria-label': 'Filtrar por local' }} sx={sel}>
          {f.placeOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      )}
      {(f.statusOptions || []).length > 0 && (
        <Select value={f.status || ''} displayEmpty onChange={(e) => a.onStatusChange?.(e.target.value)} size="small" inputProps={{ 'aria-label': 'Filtrar pela situação escolhida' }} sx={{ ...sel, flex: '0 1 170px', minWidth: 150 }}>
          {f.statusOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      )}
      <Button onClick={() => a.onClearFilters?.()} disabled={!f.canClear} startIcon={<FilterAltOffRounded sx={{ fontSize: 18 }} />} sx={{ ...OUTLINE_BTN_SX, minHeight: 40 }}>
        Limpar
      </Button>
    </Box>
  );
}

function GroupHead({ group, id }) {
  const Icon = group.id === 'shared' ? PeopleAltRounded : CalendarMonthRounded;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.25 }}>
      <Icon sx={{ fontSize: 20, color: 'var(--text-secondary)', mt: '1px' }} aria-hidden="true" />
      <Box sx={{ minWidth: 0 }}>
        <Typography id={id} variant="subtitle2" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)' }}>{group.title}</Typography>
        {group.subtitle && <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)' }}>{group.subtitle}</Typography>}
      </Box>
    </Box>
  );
}

function StateBlock({ icon, title, description, action }) {
  return (
    <Box sx={{ ...CARD_SX, px: 3, py: { xs: 5, sm: 7 }, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Box sx={{ width: 64, height: 64, borderRadius: '50%', mb: 2, bgcolor: 'var(--bg-page)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
      <Typography variant="subtitle1" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', mb: 0.5 }}>{title}</Typography>
      {description && <Typography variant="body2" sx={{ color: 'var(--text-secondary)', maxWidth: 440 }}>{description}</Typography>}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

/** Ilustração do vazio: a semana acendendo e os horários nascendo — o que a agenda produz. */
const DEMO_SLOTS = ['09:00', '09:40', '10:20', '14:00', '14:40', '15:20'];
function ClientPreview() {
  const lit = [true, true, true, true, true, false, false];
  const cycle = '7s';
  return (
    <Box aria-hidden="true" sx={{ width: '100%', maxWidth: 320, mx: 'auto' }}>
      <Box sx={{ bgcolor: 'var(--surface)', border: '1px solid', borderColor: 'var(--divider)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-hover)', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'var(--color-primary-soft)' }} />
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ height: 8, width: '58%', borderRadius: 4, bgcolor: 'var(--divider)' }} />
            <Box sx={{ height: 6, width: '36%', borderRadius: 4, bgcolor: 'var(--bg-page)' }} />
          </Box>
        </Box>
        <Typography variant="caption" sx={{ ...HEAD, color: 'var(--text-primary)' }}>Horários disponíveis</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {DAY_INITIAL.map((d, i) => (
            <Box
              key={i}
              sx={{
                flex: 1, height: 30, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', ml: i === 5 ? 0.5 : 0,
                bgcolor: lit[i] ? 'var(--color-primary-soft)' : 'var(--bg-page)',
                color: lit[i] ? 'var(--color-primary-dark)' : 'var(--text-disabled)',
                ...(lit[i] && {
                  '@keyframes schDay': {
                    '0%, 6%': { backgroundColor: 'var(--bg-page)', color: 'var(--text-disabled)' },
                    '14%, 90%': { backgroundColor: 'var(--color-primary-soft)', color: 'var(--color-primary-dark)' },
                    '97%, 100%': { backgroundColor: 'var(--bg-page)', color: 'var(--text-disabled)' },
                  },
                  animation: `schDay ${cycle} ease-in-out infinite both`,
                  animationDelay: `${i * 180}ms`,
                  [REDUCED]: { animation: 'none' },
                }),
              }}
            >
              <Typography variant="caption" sx={HEAD}>{d}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
          {DEMO_SLOTS.map((h, i) => (
            <Box
              key={h}
              sx={{
                py: 0.75, borderRadius: 999, textAlign: 'center', border: '1px solid', borderColor: 'var(--color-primary-light)', color: 'var(--color-primary-dark)',
                '@keyframes schSlot': {
                  '0%, 20%': { opacity: 0, transform: 'translateY(4px)' },
                  '28%, 90%': { opacity: 1, transform: 'none' },
                  '97%, 100%': { opacity: 0, transform: 'translateY(4px)' },
                },
                animation: `schSlot ${cycle} ease-out infinite both`,
                animationDelay: `${i * 120}ms`,
                [REDUCED]: { animation: 'none' },
              }}
            >
              <Typography variant="caption" sx={{ ...HEAD, ...NUM }}>{h}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Typography variant="caption" component="p" sx={{ color: 'var(--text-secondary)', textAlign: 'center', mt: 1.25 }}>
        É da agenda que saem os horários que o seu cliente vê.
      </Typography>
    </Box>
  );
}

const SETUP_STEPS = [
  { key: 'place', label: 'Local', Icon: PlaceRounded },
  { key: 'service', label: 'Serviço', Icon: DesignServicesRounded },
  { key: 'pro', label: 'Profissional', Icon: PersonRounded },
  { key: 'schedule', label: 'Agenda', Icon: CalendarMonthRounded },
];

function EmptyHero({ empty, a }) {
  const isSetup = empty.kind === 'no_place';
  return (
    <Box sx={{ ...CARD_SX, ...rise(0), containerType: 'inline-size', overflow: 'hidden' }}>
      <Box
        sx={{
          p: { xs: 2.5, sm: 4 }, display: 'grid', gap: 4, alignItems: 'center', gridTemplateColumns: 'minmax(0, 1fr)',
          '@container (min-width: 760px)': { gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)', gap: 6, p: 5 },
        }}
      >
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 560 }}>
          <Typography variant="h5" component="h2" sx={{ ...HEAD, color: 'var(--text-primary)', textWrap: 'pretty' }}>{empty.title}</Typography>
          <Typography variant="body1" sx={{ color: 'var(--text-secondary)', textWrap: 'pretty' }}>{empty.message}</Typography>
          {isSetup && (
            <Box component="ol" aria-label="O que o assistente cria" sx={{ listStyle: 'none', m: 0, p: 0, mt: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
              {SETUP_STEPS.map((s, i) => (
                <Box component="li" key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.75, borderRadius: 999, bgcolor: i === 3 ? 'var(--color-primary-soft)' : 'var(--bg-page)', color: i === 3 ? 'var(--color-primary-dark)' : 'var(--text-primary)' }}>
                    <s.Icon sx={{ fontSize: 16 }} aria-hidden="true" />
                    <Typography variant="caption" sx={{ fontWeight: 'var(--fw-medium)' }}>{s.label}</Typography>
                  </Box>
                  {i < SETUP_STEPS.length - 1 && <ArrowForwardRounded aria-hidden="true" sx={{ fontSize: 14, color: 'var(--text-disabled)' }} />}
                </Box>
              ))}
            </Box>
          )}
          <Box sx={{ mt: 1.5 }}>
            <Button
              onClick={() => a.onEmptyAction?.(empty.actionId)}
              disableElevation
              startIcon={isSetup ? <AutoAwesomeRounded /> : <AddRounded />}
              sx={CTA_BTN_SX}
            >
              {empty.actionLabel}
            </Button>
          </Box>
        </Box>
        <ClientPreview />
      </Box>
    </Box>
  );
}

const SK = { bgcolor: 'var(--bg-page)' };

/** Skeleton com a FORMA do que vai chegar: veredito + régua, filtros, cards com a semana. */
function LoadingSkeleton() {
  return (
    <Box aria-busy="true" aria-label="Carregando agendas">
      <Box sx={{ ...CARD_SX, p: 2.25, mb: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="circular" width={30} height={30} sx={SK} />
          <Skeleton variant="text" width="min(360px, 70%)" height={32} sx={SK} />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          {[150, 130].map((w, i) => <Skeleton key={i} variant="rounded" width={w} height={44} sx={{ ...SK, borderRadius: 'var(--radius-sm)' }} />)}
          <Box sx={{ flex: '1 1 200px', display: 'flex', gap: '3px', ml: { md: 1.5 } }}>
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} variant="rounded" height={8} sx={{ ...SK, flex: 1, maxWidth: 72, borderRadius: 3 }} />)}
          </Box>
        </Box>
      </Box>
      <Skeleton variant="rounded" height={68} sx={{ ...SK, bgcolor: 'var(--surface)', border: '1px solid var(--divider)', borderRadius: 'var(--radius-card)', mb: 'var(--space-3)' }} />
      <Box sx={CALM_GRID}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Box key={i} sx={{ ...CARD_SX, p: 2.25, display: 'flex', flexDirection: 'column', gap: 1.25, '& .MuiSkeleton-root': { animationDelay: `${i * 140}ms` } }}>
            <Skeleton variant="text" width="46%" sx={SK} />
            <Skeleton variant="text" width={`${58 + (i % 3) * 10}%`} height={26} sx={SK} />
            <Skeleton variant="text" width="72%" sx={SK} />
            <Box sx={{ display: 'flex', gap: 0.5, maxWidth: 280 }}>
              {DAY_INITIAL.map((_, d) => <Skeleton key={d} variant="rounded" height={28} sx={{ ...SK, flex: 1, borderRadius: '6px', ml: d === 5 ? 0.5 : 0 }} />)}
            </Box>
            <Skeleton variant="text" width="52%" sx={SK} />
            <Skeleton variant="text" width="40%" sx={SK} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// Defeitos em largura dupla (ímpar final ocupa a linha toda); calmos em auto-fill.
const ALERT_GRID = {
  display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'minmax(0, 1fr)',
  '@media (min-width:1400px)': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    '& > article:last-of-type:nth-of-type(odd)': { gridColumn: '1 / -1' },
  },
};
const CALM_GRID = {
  display: 'grid', gap: 'var(--space-2)', alignItems: 'stretch',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 290px), 1fr))',
};

/* ═══════════════════════════════════════════════════════════════════════════
   View
   ═══════════════════════════════════════════════════════════════════════════ */

export default function SchedulesView({ vm, actions }) {
  const a = actions || {};
  const [openWhy, setOpenWhy] = useState({});      // UI: { [scheduleId]: checklistItemId }
  const [openShifts, setOpenShifts] = useState({}); // UI: { [scheduleId]: bool }
  const [openFacts, setOpenFacts] = useState({});   // UI: { [scheduleId]: bool }
  const [flash, setFlash] = useState(null);         // UI: card realçado após "pular"
  const [celebrate, setCelebrate] = useState({});   // UI: agenda que acabou de voltar a operar
  const cardRefs = useRef({});
  const prevWeights = useRef({});
  const timers = useRef([]);

  const groups = vm?.groups || [];
  const allItems = groups.flatMap((g) => g.items || []);
  const firstAlert = allItems.find((it) => weightOf(it.operation) === 'alert');

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // alert → live: o card vencedor ganha um halo verde curto. Só UI; o dado vem do vm.
  useEffect(() => {
    const next = {};
    const won = [];
    allItems.forEach((it) => {
      const w = weightOf(it.operation);
      next[it.id] = w;
      if (prevWeights.current[it.id] === 'alert' && w === 'live') won.push(it.id);
    });
    prevWeights.current = next;
    if (won.length) {
      setCelebrate((c) => ({ ...c, ...Object.fromEntries(won.map((id) => [id, true])) }));
      timers.current.push(setTimeout(() => setCelebrate((c) => {
        const n = { ...c };
        won.forEach((id) => { delete n[id]; });
        return n;
      }), 1800));
    }
  }, [vm?.groups]); // eslint-disable-line react-hooks/exhaustive-deps

  const jump = (id) => {
    const el = cardRefs.current[id];
    if (!el) return;
    el.focus({ preventScroll: false });
    setFlash(id);
    timers.current.push(setTimeout(() => setFlash((f) => (f === id ? null : f)), 1800));
  };

  const uiFor = (id) => ({
    openWhy: openWhy[id],
    toggleWhy: (itemId) => setOpenWhy((s) => ({ ...s, [id]: s[id] === itemId ? null : itemId })),
    shiftsOpen: !!openShifts[id],
    toggleShifts: () => setOpenShifts((s) => ({ ...s, [id]: !s[id] })),
    factsOpen: !!openFacts[id],
    toggleFacts: () => setOpenFacts((s) => ({ ...s, [id]: !s[id] })),
  });

  const displayOrder = groups.flatMap((g) => {
    const items = g.items || [];
    return [...items.filter((it) => weightOf(it.operation) === 'alert'), ...items.filter((it) => weightOf(it.operation) !== 'alert')];
  });
  const roster = displayOrder.map((it) => ({
    id: it.id, name: it.name, headline: it.operation?.headline,
    weight: weightOf(it.operation), tone: alertTone(it.operation),
  }));

  const newButton = (
    <Button onClick={() => a.onNew?.()} startIcon={<AddRounded />} disableElevation sx={PRIMARY_BTN_SX}>
      {vm.actionLabel}
    </Button>
  );

  let body = null;

  if (vm.viewState === 'loading') {
    body = <LoadingSkeleton />;
  } else if (vm.viewState === 'error') {
    body = (
      <StateBlock
        icon={<ErrorRounded sx={{ fontSize: 32, color: 'var(--color-error)' }} />}
        title="Não foi possível carregar as agendas"
        description={vm.errorMessage}
        action={<Button onClick={() => a.onRetry?.()} startIcon={<ReplayRounded />} sx={{ ...OUTLINE_BTN_SX, minHeight: 42, borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)', fontWeight: 'var(--fw-bold)' }}>Tentar novamente</Button>}
      />
    );
  } else if (vm.viewState === 'empty' && vm.emptyState) {
    body = <EmptyHero empty={vm.emptyState} a={a} />;
  } else if (allItems.length === 0) {
    const e = vm.emptyState;
    body = (
      <StateBlock
        icon={<SearchOffRounded sx={{ fontSize: 30, color: 'var(--text-secondary)' }} />}
        title={e?.title || 'Nenhuma agenda encontrada'}
        description={e?.message}
        action={e?.actionId ? (
          <Button onClick={() => (e.actionId === 'clearFilters' ? a.onClearFilters?.() : a.onEmptyAction?.(e.actionId))} startIcon={<FilterAltOffRounded sx={{ fontSize: 18 }} />} sx={{ ...OUTLINE_BTN_SX, minHeight: 42 }}>
            {e.actionLabel}
          </Button>
        ) : null}
      />
    );
  } else {
    let n = 0;
    body = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {groups.map((g) => {
          const items = g.items || [];
          if (items.length === 0) return null;
          const alerts = items.filter((it) => weightOf(it.operation) === 'alert');
          const rest = items.filter((it) => weightOf(it.operation) !== 'alert');
          const headId = `grp-${g.id}`;
          return (
            <Box component="section" key={g.id} aria-labelledby={groups.length > 1 ? headId : undefined} aria-label={groups.length > 1 ? undefined : g.title}>
              {groups.length > 1 && <GroupHead group={g} id={headId} />}
              {alerts.length > 0 && (
                <Box sx={{ ...ALERT_GRID, mb: rest.length ? 'var(--space-2)' : 0 }}>
                  {alerts.map((it) => (
                    <AlertCard key={it.id} item={it} a={a} ui={uiFor(it.id)} cardRef={(el) => { cardRefs.current[it.id] = el; }} flash={flash === it.id} delay={Math.min(n++ * 50, 400)} />
                  ))}
                </Box>
              )}
              {rest.length > 0 && (
                <Box sx={CALM_GRID}>
                  {rest.map((it) => (
                    <CalmCard key={it.id} item={it} a={a} ui={uiFor(it.id)} cardRef={(el) => { cardRefs.current[it.id] = el; }} flash={flash === it.id} celebrate={!!celebrate[it.id]} delay={Math.min(n++ * 40, 400)} />
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  }

  const showSummary = vm.viewState === 'ready';
  const showFilters = vm.viewState === 'ready';

  return (
    <Box sx={{ color: 'var(--text-primary)' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 'var(--space-3)' }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonthRounded sx={{ fontSize: 30, color: 'var(--color-primary-dark)' }} aria-hidden="true" />
            <Typography variant="h1" component="h1" sx={{ color: 'var(--text-primary)', textWrap: 'pretty' }}>{vm.title}</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 0.5 }}>{vm.subtitle}</Typography>
        </Box>
        {vm.viewState !== 'empty' && newButton}
      </Box>

      {showSummary && (
        <VerdictBand
          summary={vm.summary}
          roster={roster}
          onJump={jump}
          firstAlertId={firstAlert?.id}
          firstAlertTone={alertTone(firstAlert?.operation)}
        />
      )}
      {showFilters && <FiltersBar filters={vm.filters} a={a} />}
      {body}
    </Box>
  );
}
